// ---------------------------------------------------------------------------
// Section 8: Effects — opacity, shadow
// ---------------------------------------------------------------------------

import { InspectorSection } from '@/components/inspector'
import { InlineSelectField, SHADOW_OPTIONS } from '../fields'
import {
  extractOpacity,
  setOpacity,
  extractShadow,
  setShadow,
} from '@/features/page-editor/lib/class-utils'
import type { CogoElement } from '@/features/page-editor/store/types'

export function EffectsSection({
  element,
  updateClass,
}: {
  element: CogoElement
  updateClass: (transform: (cls: string) => string) => void
}) {
  const cls = element.className ?? ''

  const opacityVal = extractOpacity(cls) ?? 100
  const shadowVal = extractShadow(cls)

  // Auto-collapse if opacity is default (100) and no shadow
  const hasValues = opacityVal !== 100 || !!shadowVal

  return (
    <InspectorSection title="Effects" defaultOpen={hasValues}>
      {/* Opacity — Framer-style slider + number */}
      <div className="flex justify-between items-center gap-2">
        <span className="text-muted-foreground text-xs shrink-0">Opacity</span>
        <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
          <input
            type="number"
            className="w-10 h-6 text-center text-xs border border-input rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            value={opacityVal}
            min={0}
            max={100}
            onChange={(e) => {
              const raw = Number(e.target.value)
              if (!Number.isNaN(raw)) {
                updateClass((c) => setOpacity(c, Math.max(0, Math.min(100, raw))))
              }
            }}
          />
          <input
            type="range"
            className="w-20 h-1 accent-primary cursor-pointer"
            min={0}
            max={100}
            step={1}
            value={opacityVal}
            onChange={(e) => updateClass((c) => setOpacity(c, Number(e.target.value)))}
          />
        </div>
      </div>

      {/* Shadow */}
      <InlineSelectField
        label="Shadow"
        value={extractShadow(cls)}
        onChange={(val) => updateClass((c) => setShadow(c, val))}
        options={SHADOW_OPTIONS}
        show
      />
    </InspectorSection>
  )
}
